import React, { useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Grid, Card, CardContent, Button, Chip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  CircularProgress, Alert, IconButton, Tooltip
} from '@mui/material';
import { Refresh, AccountBalance, Visibility, CheckCircle, Cancel, Payment } from '@mui/icons-material';
import axios from 'axios';

const API = 'https://api.bharatdarshan.app/api';

const Settlements = () => {
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState({ data: [], totals: {} });
  const [withdrawals, setWithdrawals] = useState([]);
  const [openDetail, setOpenDetail] = useState(null);
  const [openWithdrawal, setOpenWithdrawal] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [payoutRef, setPayoutRef] = useState('');
  const [adminNote, setAdminNote] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [s, w] = await Promise.all([
        axios.get(`${API}/marketplace/admin/settlements/summary`),
        axios.get(`${API}/vendor/admin/withdrawals`),
      ]);
      setSummary(s.data);
      setWithdrawals(w.data?.data || []);
    } catch (e) { setSnack({ type: 'error', msg: e.message }); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openVendorDetail = async (vendorId) => {
    try {
      const res = await axios.get(`${API}/marketplace/admin/settlements/vendor/${vendorId}`);
      setOpenDetail(res.data?.data || { orders: [], withdrawals: [] });
    } catch (e) { setSnack({ type: 'error', msg: e.message }); }
  };

  const updateWithdrawal = async (action) => {
    if (!openWithdrawal) return;
    setActionLoading(true);
    try {
      const body = { action };
      if (action === 'pay') body.payoutRef = payoutRef || `MAN${Date.now()}`;
      if (adminNote) body.adminNote = adminNote;
      await axios.put(`${API}/vendor/admin/withdrawal/${openWithdrawal._id}`, body);
      setSnack({ type: 'success', msg: `Withdrawal ${action} done` });
      setOpenWithdrawal(null); setPayoutRef(''); setAdminNote('');
      await load();
    } catch (e) { setSnack({ type: 'error', msg: e.response?.data?.message || e.message }); }
    finally { setActionLoading(false); }
  };

  const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
  const statusColor = (s) => ({ pending: 'warning', approved: 'info', paid: 'success', rejected: 'error' }[s] || 'default');

  if (loading) return <Box sx={{ p: 6, textAlign: 'center' }}><CircularProgress /></Box>;
  const totals = summary.totals || {};

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#4CAF50' }}>Vendor Settlements & Payouts</Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={load}>Refresh</Button>
      </Box>

      {snack && <Alert severity={snack.type} sx={{ mb: 2 }} onClose={() => setSnack(null)}>{snack.msg}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Revenue', value: inr(totals.totalRevenue), color: '#1976D2' },
          { label: 'Platform Commission', value: inr(totals.commission), color: '#9C27B0' },
          { label: 'Net Payable', value: inr(totals.netPayable), color: '#FF9800' },
          { label: 'Already Paid', value: inr(totals.totalWithdrawn), color: '#4CAF50' },
          { label: 'Available Payout', value: inr(totals.availablePayout), color: '#E91E63' },
        ].map((s, i) => (
          <Grid item xs={12} sm={6} md={2.4} key={i}>
            <Card sx={{ bgcolor: s.color, color: 'white' }}>
              <CardContent>
                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>{s.value}</Typography>
                <Typography variant="body2">{s.label}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <AccountBalance color="primary" />
          <Typography variant="h6">Vendor-wise Eligible Settlements</Typography>
          <Chip label={`Cycle: ${summary.cycleDays}d`} size="small" sx={{ ml: 1 }} />
          <Chip label={`Commission: ${(summary.commissionRate * 100).toFixed(0)}%`} size="small" />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><b>Vendor</b></TableCell>
                <TableCell><b>Phone</b></TableCell>
                <TableCell><b>Bank</b></TableCell>
                <TableCell align="right"><b>Orders</b></TableCell>
                <TableCell align="right"><b>Revenue</b></TableCell>
                <TableCell align="right"><b>Commission</b></TableCell>
                <TableCell align="right"><b>Net</b></TableCell>
                <TableCell align="right"><b>Withdrawn</b></TableCell>
                <TableCell align="right"><b>Available</b></TableCell>
                <TableCell><b></b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(summary.data || []).map((v) => (
                <TableRow key={v.vendorId} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{v.businessName}</Typography>
                    <Typography variant="caption" color="textSecondary">{v.ownerName}</Typography>
                  </TableCell>
                  <TableCell>{v.phone}</TableCell>
                  <TableCell>
                    {v.bankAccountNumber ? (
                      <>
                        <Typography variant="caption">{v.accountHolderName}</Typography><br />
                        <Typography variant="caption">A/C: {v.bankAccountNumber}</Typography><br />
                        <Typography variant="caption">IFSC: {v.ifscCode}</Typography>
                      </>
                    ) : <Chip size="small" label="No bank" color="warning" />}
                  </TableCell>
                  <TableCell align="right">{v.eligibleOrdersCount}</TableCell>
                  <TableCell align="right">{inr(v.totalRevenue)}</TableCell>
                  <TableCell align="right" sx={{ color: '#9C27B0' }}>{inr(v.commission)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{inr(v.netPayable)}</TableCell>
                  <TableCell align="right">{inr(v.totalWithdrawn)}</TableCell>
                  <TableCell align="right" sx={{ color: '#4CAF50', fontWeight: 700 }}>{inr(v.availablePayout)}</TableCell>
                  <TableCell>
                    <Tooltip title="View orders"><IconButton size="small" onClick={() => openVendorDetail(v.vendorId)}><Visibility /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
              {(summary.data || []).length === 0 && (
                <TableRow><TableCell colSpan={10} align="center"><Typography color="textSecondary" sx={{ py: 3 }}>No eligible settlements yet</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Payment color="primary" />
          <Typography variant="h6">Withdrawal Requests</Typography>
          <Chip label={`${withdrawals.length} total`} size="small" />
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                <TableCell><b>Date</b></TableCell>
                <TableCell><b>Vendor</b></TableCell>
                <TableCell align="right"><b>Amount</b></TableCell>
                <TableCell align="right"><b>Net</b></TableCell>
                <TableCell><b>Status</b></TableCell>
                <TableCell><b>Ref</b></TableCell>
                <TableCell><b>Note</b></TableCell>
                <TableCell><b>Action</b></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {withdrawals.map((w) => (
                <TableRow key={w._id} hover>
                  <TableCell>{new Date(w.createdAt).toLocaleString('en-IN')}</TableCell>
                  <TableCell>
                    {w.vendorId?.businessName || (typeof w.vendorId === 'string' ? w.vendorId.slice(-8) : w.vendorId?._id?.slice(-8))}<br />
                    <Typography variant="caption" color="textSecondary">{w.vendorId?.phone}</Typography>
                  </TableCell>
                  <TableCell align="right">{inr(w.amount)}</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 600 }}>{inr(w.netPayable)}</TableCell>
                  <TableCell><Chip size="small" label={w.status} color={statusColor(w.status)} /></TableCell>
                  <TableCell><Typography variant="caption">{w.payoutRef || '—'}</Typography></TableCell>
                  <TableCell><Typography variant="caption">{w.adminNote || '—'}</Typography></TableCell>
                  <TableCell>
                    {w.status === 'pending' && <Button size="small" variant="contained" onClick={() => setOpenWithdrawal(w)}>Process</Button>}
                    {w.status === 'approved' && <Button size="small" variant="contained" color="success" onClick={() => setOpenWithdrawal(w)}>Mark Paid</Button>}
                  </TableCell>
                </TableRow>
              ))}
              {withdrawals.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center"><Typography color="textSecondary" sx={{ py: 3 }}>No withdrawal requests yet</Typography></TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={!!openDetail} onClose={() => setOpenDetail(null)} maxWidth="md" fullWidth>
        <DialogTitle>Eligible Orders</DialogTitle>
        <DialogContent dividers>
          {openDetail && (
            <>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Delivered Orders ({openDetail.orders?.length || 0})</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Invoice</TableCell><TableCell>Date</TableCell><TableCell align="right">Amount</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(openDetail.orders || []).map(o => (
                      <TableRow key={o._id}>
                        <TableCell>{o.invoiceId || o._id?.slice(-8)}</TableCell>
                        <TableCell>{new Date(o.createdAt).toLocaleDateString('en-IN')}</TableCell>
                        <TableCell align="right">{inr(o.totalPaid || o.grandTotal || o.totalAmount)}</TableCell>
                        <TableCell><Chip size="small" label={o.status} color="success" /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Withdrawal History ({openDetail.withdrawals?.length || 0})</Typography>
              <TableContainer>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Date</TableCell><TableCell align="right">Amount</TableCell><TableCell>Status</TableCell><TableCell>Ref</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(openDetail.withdrawals || []).map(w => (
                      <TableRow key={w._id}>
                        <TableCell>{new Date(w.createdAt).toLocaleDateString('en-IN')}</TableCell>
                        <TableCell align="right">{inr(w.netPayable)}</TableCell>
                        <TableCell><Chip size="small" label={w.status} color={statusColor(w.status)} /></TableCell>
                        <TableCell>{w.payoutRef || '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </DialogContent>
        <DialogActions><Button onClick={() => setOpenDetail(null)}>Close</Button></DialogActions>
      </Dialog>

      <Dialog open={!!openWithdrawal} onClose={() => !actionLoading && setOpenWithdrawal(null)} maxWidth="sm" fullWidth>
        <DialogTitle>Process Withdrawal</DialogTitle>
        <DialogContent dividers>
          {openWithdrawal && (
            <Box>
              <Typography variant="body2"><b>Amount:</b> {inr(openWithdrawal.amount)} | <b>Net:</b> {inr(openWithdrawal.netPayable)}</Typography>
              <Typography variant="body2" sx={{ mt: 1 }}><b>Vendor:</b> {openWithdrawal.vendorId?.businessName || ''}</Typography>
              <Typography variant="body2"><b>Status:</b> <Chip size="small" label={openWithdrawal.status} /></Typography>
              <TextField fullWidth label="Payout Reference (NEFT/UTR)" value={payoutRef} onChange={(e) => setPayoutRef(e.target.value)} sx={{ mt: 2 }} placeholder="Auto if blank" />
              <TextField fullWidth label="Admin Note" value={adminNote} onChange={(e) => setAdminNote(e.target.value)} sx={{ mt: 2 }} multiline rows={2} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenWithdrawal(null)} disabled={actionLoading}>Cancel</Button>
          {openWithdrawal?.status === 'pending' && (
            <>
              <Button color="error" startIcon={<Cancel />} onClick={() => updateWithdrawal('reject')} disabled={actionLoading}>Reject</Button>
              <Button color="info" startIcon={<CheckCircle />} variant="contained" onClick={() => updateWithdrawal('approve')} disabled={actionLoading}>Approve</Button>
            </>
          )}
          <Button color="success" startIcon={<Payment />} variant="contained" onClick={() => updateWithdrawal('pay')} disabled={actionLoading}>Mark Paid</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Settlements;
